import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie';
import { environment } from '../../environments/environment';

@Injectable()
export class LocalizationService {

  private _daysOfWeekShort = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  private _daysOfWeekLong = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  // TODO get from locale ?
  private _firstDayOfWeek = 1;
  private _cssClassName;

  constructor(
    private translateService: TranslateService,
    private cookieService: CookieService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.initialize();
  }

  initialize(): void {
    this._fillDaysOfWeek();
    if (this.currentLang) {
      this.cookieService.put('webshop_lang', this.currentLang.replace('-', '_'));
    }
    this._updateBodyLangClassName();
  }

  private _updateBodyLangClassName(): void {
    const cssClassName = this.currentLang ? 'lang-' + this.currentLang.slice(0, 2): '';
    if (this._cssClassName && this._cssClassName !== cssClassName) {
      this.document.body.classList.remove(this._cssClassName);
    }

    if (this._cssClassName !== cssClassName && cssClassName) {
      this.document.body.classList.add(cssClassName);
      this._cssClassName = cssClassName;
    }
  }

  private _fillDaysOfWeek(): void {
    const wd = new Date();
    const dowShortIndexToText = new Map<number, string>();
    const dowLongIndexToText = new Map<number, string>();
    for (let i = 0; i < 7; i++) {
      wd.setDate(wd.getDate() + 1);
      dowShortIndexToText[wd.getDay()] = this.getWeekdayNameShortByDate(wd);
      dowLongIndexToText[wd.getDay()] = this.getWeekdayNameLongByDate(wd);
    }
    const daysOfWeekShort = [];
    const daysOfWeekLong = [];

    for (let i = 0; i < 7 + this.firstDayOfWeek; i++) {
      daysOfWeekShort.push(dowShortIndexToText[i]);
      daysOfWeekLong.push(dowLongIndexToText[i]);
    }

    this._daysOfWeekShort = daysOfWeekShort;
    this._daysOfWeekLong = daysOfWeekLong;
  }

  get currentLang(): string {
    return this.translateService.currentLang;
  }

  get daysOfWeekShort(): string[] {
    return this._daysOfWeekShort;
  }

  get daysOfWeekLong(): string[] {
    return this._daysOfWeekLong;
  }

  get firstDayOfWeek(): number {
    return this._firstDayOfWeek;
  }

  formatDate(date: Date): string {
    return date ? date.toLocaleDateString(this.translateService.currentLang, environment.globalSettings.dateFormat) : '';
  }

  formatDatetime(date: Date): string {
    return date ? date.toLocaleDateString(this.translateService.currentLang, environment.globalSettings.datetimeFormat) : '';
  }

  formatDateToDayMonth(date: Date): string {
    return date ? date.toLocaleDateString(this.translateService.currentLang, environment.globalSettings.onlyDayMonthFormat) : '';
  }

  formatDateToShotWeekdayAndDate(date: Date): string {
    return date ? date.toLocaleString(this.translateService.currentLang, environment.globalSettings.shotWeekdayAndDateFormat) : '?';
  }

  getWeekdayNameShortByIndex(idx: number): string {
    return idx < 0 || idx > 6 ? '?' : this.daysOfWeekShort[idx];
  }

  getWeekdayNameLongByIndex(idx: number): string {
    return idx < 0 || idx > 6 ? '?' : this.daysOfWeekLong[idx];
  }

  getWeekdayNameShortByDate(date: Date): string {
    return date ? date.toLocaleString(this.translateService.currentLang, { weekday: 'short' }) : '?';
  }

  getWeekdayNameLongByDate(date: Date): string {
    return date ? date.toLocaleString(this.translateService.currentLang, { weekday: 'long' }) : '?';
  }

  getMonthNameShortByDate(date: Date): string {
    return date ? date.toLocaleString(this.translateService.currentLang, { month: 'short' }) : '?';
  }

  getMonthNameLongByDate(date: Date): string {
    return date ? date.toLocaleString(this.translateService.currentLang, { month: 'long' }) : '?';
  }

  compareDaysOfWeek(dayA: number, dayB: number): number {

    if (dayA < this._firstDayOfWeek) {
      dayA += 7;
    }

    if (dayB < this._firstDayOfWeek) {
      dayB += 7;
    }
    return dayA - dayB;
  }

  getTodayIndex(): number {
    const date = new Date();
    return date.getDay();
  }

  get availableLangs(): string[] {
    const availableLangs = environment.availableLangs.length > 0 ? environment.availableLangs : ['de-DE'];
    return availableLangs;
  }

  setLanguage(isoLanguage: string): void {
    if (isoLanguage && this.availableLangs.includes(isoLanguage)) {
      this.translateService.use(isoLanguage).subscribe(() => {
        this.initialize();
      });
    }
  }

}
